import { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Stack, StackProps, Typography, useMediaQuery, useTheme } from "@mui/material";
import { API_URL, APP_COMPANY_INFO_LINK, APP_COMPANY_NAME } from "consts";
import { PortfolioData } from "types";
import getStyles from "./styles";

type Props = StackProps & {
  families: { data: PortfolioData | null };
};

// @todo: Find a way to solve this without hardcoding company names and links in the code. Maybe in the .env file or something similar, to be able to change it without code changes in the future.
const getInfoLink = (language: string) => {
  if (APP_COMPANY_NAME.toLowerCase() !== "signify") return APP_COMPANY_INFO_LINK;
  // These other links are just for Signify, as they have different pages for different languages. For other companies, we assume the link in the .env file is correct for all languages.
  if (language === "de") return `${APP_COMPANY_INFO_LINK}de-de/specifier`;
  if (language === "fr") return `${APP_COMPANY_INFO_LINK}fr-fr/specifier`;
  return `${APP_COMPANY_INFO_LINK}en-gb/specifier`;
};

const Header: FC<Props> = ({ families, ...rest }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.down("md"));
  const styles = useMemo(() => getStyles(theme).Header, [theme]);
  const language = i18n.resolvedLanguage;

  const href = useMemo(() => getInfoLink(language), [language]);

  const showDownload = useMemo(() => families?.data?.portfolio.id !== null && !!families?.data?.portfolio.zip_file, [families]);

  return (
    <Stack sx={styles.container} {...rest}>
      <Alert variant="outlined" severity="success" icon={false} sx={styles.alert}>
        <Typography variant="body2">
          {t("Welcome to this web-service from {{company}}. We help you to design your road. For further information, click on the info button.", {
            company: APP_COMPANY_NAME,
          })}
        </Typography>
        <Button component="a" href={href} target="_blank" variant="contained" size="small" sx={styles.link}>
          {t("Info")}
        </Button>
      </Alert>
      {showDownload && (
        <Button
          href={`${API_URL}/api/portfolios/${families?.data?.portfolio.id}/download`}
          variant="contained"
          size={md ? "small" : "large"}
          color="primary"
          sx={styles.button}
        >
          {t("Download all LDT (.zip)")}
        </Button>
      )}
    </Stack>
  );
};

export default memo(Header);
